import { assertUnreachable } from "@lib/utils/assertUnreachable";
import {
  assertGetEvaluator,
  assertGetMember,
  isFunderRatingPairEligible,
  isMemberTrackComplete,
  isTrackStatusComplete,
  isTrackStatusFrozen,
} from "./accessors";
import type { StampableAction } from "./actions";
import type { RoundAggregate } from "./aggregate";

/**
 * Raised by the reducer when an action fails its permission check. Subclass of
 * Error so existing `instanceof Error` handling (websocket, replay, confirm)
 * still treats it as a failure, while callers that care — e.g. the agent API
 * endpoints — can distinguish "you're not allowed" (→ 403) from an unexpected
 * server error (→ 500) off the authoritative reducer result.
 */
/**
 * Message every action on an archived round is refused with. The check
 * itself lives in Reducer.performAction, ahead of the createdBySfcAdmin
 * fork, because it must hold regardless of who is acting — isActionPermitted
 * only runs for human-actor dispatches.
 */
export const ARCHIVED_ROUND_ERROR =
  "This round is archived and read-only. Restore it in Round Settings to make changes.";

export class ActionNotPermittedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ActionNotPermittedError";
  }
}

function canChangeFunder(
  round: RoundAggregate,
  funderId: string,
  actingAsUserId: string,
): true | string {
  const member = assertGetMember(round, actingAsUserId);
  if (member.isRoundAdmin) return true;

  const evaluator = assertGetEvaluator(round, funderId);
  return evaluator.memberId === actingAsUserId
    ? true
    : "User isn't a funder in this round";
}

/**
 * Checks whether a user-initiated action is permitted given the current round
 * state and the acting user.
 *
 * This function only handles UserActions — actions initiated by a human actor.
 *
 * This function is a defense-in-depth layer, NOT the sole access control
 * boundary. Actions reach this function from two paths:
 *
 * 1. WebSocket clients — the WebSocket server in Server.ts rejects blocked
 *    actions before they reach this function: non-creatable action types (see
 *    NON_CREATABLE_ACTION_TYPES) and server-only action types (see
 *    isServerOnlyActionType).
 * 2. Server-side dispatch (tRPC routes) — these call storage.performAction
 *    directly, bypassing the WebSocket server. Access control for these paths
 *    is enforced at the API boundary (tRPC middleware, requirePermission).
 */
export function isActionPermitted(
  round: RoundAggregate,
  action: StampableAction,
  actingAsUserId: string,
  { actionAlreadyConfirmed = false }: { actionAlreadyConfirmed?: boolean } = {},
): true | string {
  if (action.type === "CREATE_ROUND" || action.type === "CREATE_ROUND_WIZARD") {
    // Trusted admin dispatches bypass this check in the reducer. Previously
    // confirmed external creations must remain replayable.
    return actionAlreadyConfirmed ? true : "Only SFC admins can create rounds";
  }
  if (action.type === "NOOP") return true;

  const actingMember = assertGetMember(round, actingAsUserId);
  if (actingMember.status !== "active") {
    return "User must be an active round member";
  }

  switch (action.type) {
    // Round admins only:
    case "ADD_ROUND_MEMBER":
    case "ADD_PROPOSAL":
    case "ADD_PROPOSAL_FORM":
    case "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD":
    case "ADD_EVALUATOR":
    case "REMOVE_ROUND_MEMBER":
    case "REMOVE_PROPOSAL_FORM":
    case "PROPOSAL_FORM_ROUND_IDENTIFIER_CHANGE":
    case "REMOVE_PROPOSAL_FORM_FIELD":
    case "REMOVE_EVALUATOR":
    case "REMOVE_PROPOSAL":
    case "READD_PROPOSAL":
    case "PROPOSAL_FORM_FIELD_CHANGE":
    case "PROPOSAL_LONGNAME_CHANGE":
    case "PROPOSAL_SHORTNAME_CHANGE":
    case "PROPOSAL_FIELD_RESPONSE_CHANGE":
    case "PROPOSAL_FORM_EXTERNAL_SYNC":
    case "PROPOSAL_FIELD_TITLE_CHANGE":
    case "PROPOSAL_FIELD_DESCRIPTION_CHANGE":
    case "ROUND_NAME_CHANGE":
    case "ROUND_ARCHIVED_CHANGE":
    case "ROUND_APPLICATIONS_OPEN_CHANGE":
    case "SET_TRACK_SETTINGS":
    case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY":
    case "RESET_FUNDER_INCREMENTAL_EVALS":
    case "FUNDER_USES_MATCHING_CHANGE":
    case "CREATE_OR_UPDATE_ROUND_MEMBER":
    case "PROPOSAL_BASE_REQUEST_AMOUNT_CHANGE":
    case "ROUND_ADVANCED_ANALYTICS_CHANGE":
    case "IMPORT_PROPOSALS": {
      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      return "User isn't a round admin";
    }

    case "START_EVALUATION":
    case "EVALUATION_MVF_CHANGE":
    case "MATCHING_MVF_CHANGE":
    case "EVALUATION_SET_ZERO":
    case "EVALUATION_STATE_CHANGE":
    case "EVALUATION_SET_HIDDEN":
    case "EVALUATION_FINALITY_CHANGE":
    case "EVALUATION_NEGATIVE_VALUE_CHANGE":
    case "NORMALIZE_EVALUATIONS":
    case "INCREMENTAL_EVALS_PUSH":
    case "INCREMENTAL_EVALS_UNDO":
    case "INCREMENTAL_EVALS_REDO": {
      const evaluator = assertGetEvaluator(round, action.evaluatorId);

      // Mean recommender MVFs are auto-generated and should never be edited
      if (
        evaluator.type === "recommender" &&
        evaluator.meanRecommenderThreshold != null
      ) {
        return "Mean recommender evaluations cannot be edited";
      }

      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      if (evaluator.memberId === actingAsUserId) {
        if (evaluator.type === "recommender" && member.track !== "NO_TRACK") {
          const status = round.trackSettings[member.track].status;
          // The matching parameters (value multiplier + drop-off rate) stay
          // editable through evals-freeze and only lock once the track is
          // complete. The standard MVF and the other evaluation actions in
          // this group lock as soon as the track enters evals-freeze.
          if (action.type === "MATCHING_MVF_CHANGE") {
            if (isTrackStatusComplete(status)) {
              return "Track is complete";
            }
          } else if (isTrackStatusFrozen(status)) {
            return "Track is in evals freeze";
          }
        }
        return true;
      }

      return "User isn't a round admin or the evaluator taking action";
    }

    // Round admins can remove a round
    case "ONE_TIME_ADMIN_ACTION_1":
    case "ONE_TIME_ADMIN_ACTION_2":
    case "REMOVE_ROUND": {
      const member = assertGetMember(round, actingAsUserId);

      // TODO(SP-629): This should be SFC admins
      if (member.isRoundAdmin) {
        return true;
      }

      return "User isn't an SFC admin";
    }

    // Round members can change feedback and notes
    case "EVALUATION_FEEDBACK_CHANGE":
    case "EVALUATION_NOTES_CHANGE":
    case "GENERAL_NOTES_CHANGE": {
      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) return true;
      if (action.memberId !== actingAsUserId)
        return "User can't act on another user's feedback, notes, or properties";
      // Notes and feedback stay editable through evals-freeze, but the whole
      // round goes read-only at complete — enforce that here so a pending
      // autosave or a stale/direct client dispatch can't write after the UI
      // has locked.
      if (isMemberTrackComplete(round, member)) {
        return "Track is complete";
      }
      return true;
    }

    // Visibility and member properties — owner or admin
    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE":
    case "ROUND_MEMBER_PROPERTY_CHANGE": {
      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      if (action.memberId === actingAsUserId) {
        return true;
      }

      return "User can't act on another user's feedback, notes, or properties";
    }

    case "AUTHORIZE_AGENT_TOKEN_FOR_ROUND":
    case "DEAUTHORIZE_AGENT_TOKEN_FOR_ROUND": {
      return true;
    }

    case "EVALUATOR_PROPERTY_CHANGE": {
      const member = assertGetMember(round, actingAsUserId);

      if (!member) {
        return "Member not found";
      }

      if (member.isRoundAdmin) {
        return true;
      }

      const evaluator = assertGetEvaluator(round, action.evaluatorId);
      if (evaluator.memberId === actingAsUserId) {
        return true;
      }

      return "User can't act on another evaluator's properties";
    }

    // Round members can change their own investigation interest, review
    // progress, and disclosures
    case "INVESTIGATION_INTEREST_CHANGE":
    case "PROPOSAL_DISCLOSURE_CHANGE":
    case "REVIEW_PROGRESS_CHANGE": {
      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      const targetMemberId =
        action.type === "PROPOSAL_DISCLOSURE_CHANGE"
          ? action.userId
          : action.memberId;
      if (targetMemberId !== actingAsUserId) {
        return "User can't act on another user's investigation interest, review progress, or disclosures";
      }

      if (
        member.track !== "NO_TRACK" &&
        ["4.evals-freeze", "5.complete"].includes(
          round.trackSettings[member.track].status,
        )
      ) {
        return "Track is in evals freeze";
      }

      return true;
    }

    // Round admins and funders only can change funder budgets and scores
    case "FUNDER_RATINGS_IMPORT":
      if (
        !actionAlreadyConfirmed &&
        action.scores.some(
          ({ recommenderId, proposalId, rating }) =>
            rating != null &&
            !isFunderRatingPairEligible(round, recommenderId, proposalId),
        )
      ) {
        return "Funder ratings require an active evaluation with a note or MVF";
      }
      // Confirmed imports replay under the rules in force when they were accepted.
      return canChangeFunder(round, action.funderId, actingAsUserId);
    case "FUNDER_RATING_CHANGE":
      // Confirmed actions were accepted under the permission rules in force at
      // the time; replay must preserve them. Apply the new eligibility rule
      // only while confirming a fresh submission.
      if (
        !actionAlreadyConfirmed &&
        action.rating != null &&
        !isFunderRatingPairEligible(
          round,
          action.recommenderId,
          action.proposalId,
        )
      ) {
        return "Funder ratings require an active evaluation with a note or MVF";
      }
      // A clear remains allowed so stale historical scores can be removed.
      return canChangeFunder(round, action.funderId, actingAsUserId);
    case "FUNDER_BUDGET_CHANGE":
    case "FUNDER_SETTLEMENT_BUDGET_CHANGE":
    case "FUNDER_PROPOSAL_WEIGHT_CHANGE":
    case "FUNDER_PROPOSAL_EXCLUSION_TOGGLE":
    case "FUNDER_RECOMMENDER_WEIGHT_CHANGE":
    case "FUNDER_MATCHING_BUDGET_RATIO_CHANGE": {
      return canChangeFunder(round, action.funderId, actingAsUserId);
    }

    // Wiping a funder's ratings (or, in the legacy round-wide form, every
    // funder's) is SFC-admin-only. Both are marked server-only (see
    // SERVER_ONLY_ACTION_TYPES) and dispatched exclusively by the
    // isSfcAdmin-gated tRPC route with `createdBySfcAdmin`, which bypasses
    // this check. So any dispatch that actually reaches here came from a client
    // (e.g. a round admin over the WebSocket) and must be denied.
    case "FUNDER_RATINGS_CLEAR":
    case "CLEAR_FUNDER_RATINGS":
      return "Clearing funder ratings must go through the SFC-admin API route";

    // Same shape: the Application Builder is SFC-admin only, so attaching one
    // of its forms to a round and mapping its questions is too. The Proposal
    // Forms routes dispatch these with createdBySfcAdmin after checking the
    // form exists and is published; a dispatch reaching here came from a
    // client over the socket and is refused.
    case "ATTACH_APPLICATION_FORM":
    case "APPLICATION_RESPONSE_INCLUSION":
    case "DETACH_APPLICATION_FORM":
    case "APPLICATION_FORM_FIELD_MAPPING_CHANGE":
    case "APPLICATION_FORM_OPTION_VALUE_CHANGE":
      return "Application form attachments must go through the SFC-admin Proposal Forms routes";

    case "MATCHING_ADJUSTMENT_INTENSITY_CHANGE": {
      const member = assertGetMember(round, actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      for (const evaluator of Object.values(member.evaluators)) {
        if (evaluator.type === "funder") {
          return true;
        }
      }

      return "User isn't a funder in this round";
    }

    // Track members can vote on recusals
    case "RECUSAL_VOTE": {
      const { votingUserId, subjectUserId } = action;
      const actingMember = assertGetMember(round, actingAsUserId);
      if (!actingMember.isRoundAdmin && votingUserId !== actingAsUserId) {
        return "User can't vote on behalf of another user";
      }

      const votingMember = assertGetMember(round, votingUserId);
      const subjectMember = assertGetMember(round, subjectUserId);

      if (
        votingMember.track !== "NO_TRACK" &&
        ["4.evals-freeze", "5.complete"].includes(
          round.trackSettings[votingMember.track].status,
        )
      ) {
        return "Track is in evals freeze";
      }

      if (subjectMember.track === "NO_TRACK") {
        return true;
      }

      if (subjectMember.track === votingMember.track) {
        return true;
      }

      return "User must be in the same track as the member whose recusal they are voting on";
    }

    default:
      assertUnreachable(action);
  }
}
